import React, { useState, useCallback, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import LandingPage from './components/marketing/LandingPage';
import AnalysisResults from './components/AnalysisResults';
import LoadingScreen from './components/LoadingScreen';
import { analyzeCompanies, verifyMagicLink } from './services/api';
import { BRAND } from './brand';
import BrandLockup from './components/BrandLockup';
import './styles/App.css';
import './styles/landing.css';

function App() {
  useEffect(() => {
    document.title = BRAND.documentTitle;
  }, []);

  useEffect(() => {
    const path = window.location.pathname || '';
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if ((path === '/auth/verify' || path.endsWith('/auth/verify')) && token) {
      verifyMagicLink(token)
        .then(() => {
          toast.success('Signed in');
          window.history.replaceState({}, '', '/');
        })
        .catch(() => {
          toast.error('Sign-in link invalid or expired');
          window.history.replaceState({}, '', '/');
        });
    }
  }, []);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalysis = useCallback(async (companies, useCase = 'vendor', options = {}) => {
    try {
      setIsAnalyzing(true);
      setError(null);
      setAnalysisResults(null);

      const results = await analyzeCompanies(companies, useCase, options);

      if (results.success) {
        setAnalysisResults(results);
        if (results.billing?.reason === 'declined_not_charged') {
          toast(results.billing.message || 'Declined — not charged', { icon: 'ℹ️' });
        } else if (results.emailDelivery?.ok) {
          toast.success('Opinion emailed as PDF');
        } else if (results.emailDelivery && results.emailDelivery.ok === false && results.emailDelivery.message) {
          toast.error(results.emailDelivery.message);
        }
      } else {
        throw new Error(results.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'An error occurred during analysis');
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  const handleNewAnalysis = useCallback(() => {
    setAnalysisResults(null);
    setError(null);
  }, []);

  const showLanding = !isAnalyzing && !analysisResults;
  const showResults = !isAnalyzing && analysisResults;

  return (
    <div className="App">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#1e293b',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            padding: '12px 16px',
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
          }
        }}
      />

      {showLanding && (
        <LandingPage
          onAnalyze={handleAnalysis}
          error={error}
        />
      )}

      {(isAnalyzing || showResults) && (
        <>
          <div className="mk-app-chrome">
            <div className="mk-app-chrome-inner">
              <BrandLockup onClick={handleNewAnalysis} />
              {showResults && (
                <button type="button" className="mk-btn mk-btn-ghost mk-btn-sm" onClick={handleNewAnalysis}>
                  New analysis
                </button>
              )}
            </div>
          </div>

          <main className="main-content">
            {isAnalyzing && <LoadingScreen />}

            {showResults && (
              <AnalysisResults results={analysisResults} onNewAnalysis={handleNewAnalysis} />
            )}
          </main>
        </>
      )}
    </div>
  );
}

export default App;
